import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function isPWA() {
  if (typeof window === 'undefined') return false;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSPWA = window.navigator.standalone === true;
  return isStandalone || isIOSPWA;
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export function formatNumber(num) {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-IN').format(num);
}

export function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

export function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export const statusColors = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  under_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  suspended: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  needs_correction: 'bg-orange-50 text-orange-700 border-orange-200',
};

export const tierColors = {
  bronze: 'bg-amber-50 text-amber-900 border-amber-200',
  silver: 'bg-slate-100 text-slate-800 border-slate-300',
  gold: 'bg-amber-100 text-amber-950 border-amber-300',
  platinum: 'bg-blue-50 text-blue-900 border-blue-200',
};

export const categoryLabels = {
  sub_dsa: 'Sub-DSA',
  ca: 'Chartered Accountant',
  ca_firm: 'CA Firm',
  broker: 'Broker',
  builder: 'Builder',
  insurance_agent: 'Insurance Agent',
  wealth_advisor: 'Wealth Advisor',
  individual: 'Individual',
  corporate: 'Corporate',
  freelancer: 'Freelancer',
  other: 'Other'
};

export const partnerTypes = [
  { value: 'individual', label: 'Individual' },
  { value: 'entity', label: 'Business Entity' }
];

export const partnerCategories = [
  { value: 'sub_dsa', label: 'Sub-DSA' },
  { value: 'ca', label: 'Chartered Accountant' },
  { value: 'ca_firm', label: 'CA Firm' },
  { value: 'broker', label: 'Broker' },
  { value: 'builder', label: 'Builder' },
  { value: 'insurance_agent', label: 'Insurance Agent' },
  { value: 'wealth_advisor', label: 'Wealth Advisor' },
  { value: 'individual', label: 'Individual Referrer' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'other', label: 'Other' }
];

export const AGREEMENT_COPY_DOC_TYPE = 'agreement_copy';
export const AGREEMENT_COPY_LABEL = 'Agreement Copy';
export const AGREEMENT_COPY_DOC_TYPES = [AGREEMENT_COPY_DOC_TYPE, 'signed_agreement'];

export function isAgreementCopyDocument(documentType) {
  return AGREEMENT_COPY_DOC_TYPES.includes(documentType);
}

export const documentTypes = [
  { value: 'pan_card', label: 'PAN Card', required: true },
  { value: 'address_proof', label: 'Address Proof', required: true },
  { value: 'aadhaar', label: 'Aadhaar Card', required: false },
  { value: 'gst_certificate', label: 'GST Certificate', required: false },
  { value: 'bank_statement', label: 'Bank Statement', required: false },
  { value: 'registration_certificate', label: 'Company Registration Certificate', required: false },
  { value: 'cancelled_cheque', label: 'Cancelled Cheque', required: false },
  { value: 'photo', label: 'Business Portrait', required: false },
  { value: 'other', label: 'Other Document', required: false }
];

/** KYC documents shown on the Documents onboarding step (excludes Agreement Copy). */
export const kycDocumentTypes = documentTypes;

export function getDocumentTypeLabel(documentType) {
  if (!documentType) return 'Document';
  if (isAgreementCopyDocument(documentType)) return AGREEMENT_COPY_LABEL;
  const match = documentTypes.find(dt => dt.value === documentType);
  if (match?.label) return match.label;
  return documentType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export const partnerTypeLabels = {
  individual: 'Individual',
  entity: 'Business Entity'
};

export const addressTypeLabels = {
  registered: 'Registered Address',
  office: 'Office Address',
  residential: 'Residential Address',
  branch: 'Branch Address'
};

export const constitutionTypeLabels = {
  pvt_ltd: 'Private Limited',
  llp: 'LLP',
  partnership: 'Partnership',
  proprietorship: 'Proprietorship',
  individual: 'Individual',
  public_ltd: 'Public Limited',
  trust: 'Trust',
  society: 'Society',
  huf: 'HUF'
};

export const noteTypeLabels = {
  internal: 'Internal',
  general: 'General',
  followup: 'Follow-up',
  escalation: 'Escalation',
  compliance: 'Compliance'
};
